import { inject } from "ui-common/di";
import type { IWritableCreatorMessagesState, ICreatorMessagesService } from "./types";
import type { CreatorMessage } from "api-contracts/rpc/get-creator-messages";
import type { GetMessageSegmentsResult } from "api-contracts/rpc/creator-message";
import type { IRpcService } from "ui-common/services/rpc/types";
import type { SortDirection } from "ui-components/wow-base-table/types";

interface GetCreatorMessagesResponse {
  messages: CreatorMessage[];
  counts: Record<string, number>;
}

export class CreatorMessagesService implements ICreatorMessagesService {
  @inject<IWritableCreatorMessagesState>("writableCreatorMessagesState")
  private state!: IWritableCreatorMessagesState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  private isLoaded = false;

  async loadMessages(): Promise<void> {
    if (this.isLoaded) return;

    this.state.isLoading.set(true);
    this.state.errorMessage.set("");

    try {
      const [result, segments] = await Promise.all([
        this.rpcService.call<GetCreatorMessagesResponse>("get-creator-messages", {}),
        this.rpcService.call<GetMessageSegmentsResult>("get-message-segments", {}),
      ]);
      this.state.messages.set(result.messages);
      this.state.communityCount.set(segments.totalReachable);
      this.isLoaded = true;
    } catch (error) {
      this.state.errorMessage.set((error as Error).message);
    } finally {
      this.state.isLoading.set(false);
    }
  }

  updateSort(column: string, direction: SortDirection): void {
    if (direction === "none") {
      this.state.activeSorts.set(this.state.activeSorts.get().filter((s) => s.column !== column));
    } else {
      const current = this.state.activeSorts.get();
      const existing = current.findIndex((s) => s.column === column);
      if (existing >= 0) {
        const updated = [...current];
        updated[existing] = { column, direction };
        this.state.activeSorts.set(updated);
      } else {
        this.state.activeSorts.set([...current, { column, direction }]);
      }
    }
  }

  clearCache(): void {
    this.state.messages.set([]);
    this.state.errorMessage.set("");
    this.state.activeSorts.set([]);
    this.state.communityCount.set(0);
    this.isLoaded = false;
  }

  invalidateCache(): void {
    this.isLoaded = false;
  }
}
